import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import {
  mfaVerifyRequestSchema,
  type MfaVerifyRequest,
} from "@sendsms/shared";
import { api, ApiError } from "@/lib/api";
import {
  applyFieldErrors,
  translateApiError,
  translateFieldError,
} from "@/lib/form-errors";
import { meQueryKey } from "@/lib/auth/context";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  CardBody,
  CardFooter,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Checkbox } from "@/components/ui/Checkbox";

type MfaVerifyFormValues = z.input<typeof mfaVerifyRequestSchema>;

export default function MfaVerifyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [topError, setTopError] = useState<string | null>(null);
  const [setupPending, setSetupPending] = useState(false); // MFA-2: show the set-up-again button

  const form = useForm<MfaVerifyFormValues>({
    resolver: zodResolver(mfaVerifyRequestSchema),
    defaultValues: {
      code: "",
      trust_device: false,
      trust_device_label: "",
    },
    mode: "onSubmit",
  });

  const trustDevice = form.watch("trust_device");
  const currentCode = form.watch("code") ?? "";
  const cleanCode = currentCode.replace(/[\s\-]/g, "");
  const codeHint =
    cleanCode.length === 0
      ? undefined
      : cleanCode.length >= 10
        ? t("auth.mfa_verify.code_help_backup")
        : t("auth.mfa_verify.code_help_totp");

  const mutation = useMutation({
    mutationFn: (body: MfaVerifyRequest) => api.mfaVerify(body),
    onSuccess: async () => {
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== meQueryKey[0] }); // QC-LOGOUT-1: fresh cache for the new session
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      navigate("/dashboard", { replace: true });
    },
    onError: (err: unknown) => {
      // MFA-2: an unconfirmed enrollment (or none) gets a way back to setup instead of a dead end.
      if (err instanceof ApiError && (err.code === "mfa_setup_pending" || err.code === "mfa_not_setup")) setSetupPending(true);
      const handled = applyFieldErrors(t, err, form.setError);
      if (!handled) setTopError(translateApiError(t, err));
    },
  });

  const codeError = form.formState.errors.code?.message
    ? translateFieldError(t, form.formState.errors.code.message)
    : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.mfa_verify.title")}</CardTitle>
        <CardSubtitle>{t("auth.mfa_verify.subtitle")}</CardSubtitle>
      </CardHeader>

      <CardBody>
        {topError ? (
          <Alert tone="danger" description={topError} className="mb-5" />
        ) : null}

        {setupPending ? (
          <div className="mb-5">
            <Button type="button" variant="secondary" fullWidth onClick={() => navigate("/mfa/setup", { replace: true })}>
              {t("auth.mfa_verify.setup_again")}
            </Button>
          </div>
        ) : null}
        <form
          onSubmit={form.handleSubmit((data) => {
            setTopError(null);
            const payload: MfaVerifyRequest = {
              code: data.code,
              trust_device: data.trust_device ?? false,
              trust_device_label: data.trust_device
                ? data.trust_device_label?.trim() || undefined
                : undefined,
            };
            mutation.mutate(payload);
          })}
          className="space-y-4"
          noValidate
        >
          <Input
            label={t("auth.mfa_verify.code_label")}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            placeholder={t("auth.mfa_verify.code_placeholder")}
            hint={codeHint}
            {...form.register("code")}
            error={codeError}
            className="text-center font-mono text-title tracking-[0.25em]"
          />

          <div className="rounded-lg bg-surface-subtle p-4 space-y-3">
            <Checkbox
              checked={trustDevice}
              onCheckedChange={(checked) =>
                form.setValue("trust_device", checked === true)
              }
              label={t("auth.mfa_verify.trust_device_label")}
              hint={t("auth.mfa_verify.trust_device_help")}
            />

            {trustDevice ? (
              <Input
                label={t("auth.mfa_verify.device_label_label")}
                placeholder={t("auth.mfa_verify.device_label_placeholder")}
                {...form.register("trust_device_label")}
              />
            ) : null}
          </div>

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={mutation.isPending}
          >
            {t("auth.mfa_verify.submit")}
          </Button>
        </form>
      </CardBody>

      <CardFooter>
        <Link
          to="/mfa/reset-request"
          className="text-caption text-brand-500 hover:underline focus:outline-none focus-visible:shadow-focus rounded-sm"
        >
          {t("auth.mfa_verify.lost_device")}
        </Link>
      </CardFooter>
    </Card>
  );
}
